import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { announcementKeys } from "@/lib/query";
import type { AnnouncementIcon, AnnouncementRecurrence } from "@/schemas/announcements";

export interface Announcement {
  id: string;
  title: string;
  body: string;
  icon: AnnouncementIcon;
  /** Optional call-to-action button — label and URL are set together. */
  linkUrl: string | null;
  linkLabel: string | null;
  /** Optional card background (hex). */
  bgColor: string | null;
  startsAt: string;
  recurrence: AnnouncementRecurrence;
  createdByUserId: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

/** An active announcement as the dashboard sees it. */
export interface ActiveAnnouncement extends Announcement {
  /** Start of the occurrence window currently showing (differs from startsAt for recurring). */
  occurrenceStart: string;
}

/** A paginated admin list row. `active` = has it started. */
export interface AnnouncementListRow extends Announcement {
  active: boolean;
}

/** An announcement the user dismissed, as the Prior announcements dialog sees it. */
export interface PriorAnnouncement extends Announcement {
  dismissedAt: string;
}

interface ListResponse {
  data: AnnouncementListRow[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

/** ISO-string payload — the server coerces to dates. */
export interface AnnouncementPayload {
  title: string;
  body: string;
  icon: AnnouncementIcon;
  linkUrl: string | null;
  linkLabel: string | null;
  bgColor: string | null;
  startsAt: string;
  recurrence: AnnouncementRecurrence;
}

export type AnnouncementAdminFilter = "current" | "recurring";

export function useActiveAnnouncements() {
  return useQuery({
    queryKey: announcementKeys.active,
    queryFn: async (): Promise<ActiveAnnouncement[]> => {
      const res = await fetch("/api/announcements");
      if (!res.ok) throw new Error("Failed to load announcements");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useAnnouncementsAdmin(filters: {
  search: string;
  filter: AnnouncementAdminFilter;
}) {
  return useQuery({
    queryKey: announcementKeys.admin(filters),
    queryFn: async (): Promise<ListResponse> => {
      const params = new URLSearchParams({ scope: "admin", limit: "50", filter: filters.filter });
      if (filters.search) params.set("search", filters.search);
      const res = await fetch(`/api/announcements?${params}`);
      if (!res.ok) throw new Error("Failed to load announcements");
      return res.json();
    },
  });
}

/** The requesting user's dismissed announcements, newest dismissal first. */
export function usePriorAnnouncements(enabled: boolean) {
  return useQuery({
    queryKey: announcementKeys.prior,
    enabled,
    queryFn: async (): Promise<PriorAnnouncement[]> => {
      const res = await fetch("/api/announcements?scope=prior");
      if (!res.ok) throw new Error("Failed to load prior announcements");
      const json = await res.json();
      return json.data;
    },
  });
}

async function throwIfError(res: Response, fallback: string): Promise<void> {
  if (res.ok) return;
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  throw new Error(body?.error ?? fallback);
}

/** Dismiss the current showing for this user, moving it to their prior list. */
export function useDismissAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/announcements/${id}/dismiss`, { method: "POST" });
      await throwIfError(res, "Failed to dismiss announcement");
    },
    onSuccess: () => {
      // The dismissed card leaves the dashboard AND joins the prior list.
      qc.invalidateQueries({ queryKey: announcementKeys.all });
      toast.success("Announcement dismissed");
    },
    onError: (e) => toast.error(e.message),
  });
}

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AnnouncementPayload) => {
      const res = await fetch("/api/announcements", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      await throwIfError(res, "Failed to create announcement");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: announcementKeys.all });
      toast.success("Announcement created");
    },
    onError: (e) => toast.error(e.message),
  });
}

export function useUpdateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: AnnouncementPayload & { id: string }) => {
      const res = await fetch(`/api/announcements/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      await throwIfError(res, "Failed to update announcement");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: announcementKeys.all });
      toast.success("Announcement updated");
    },
    onError: (e) => toast.error(e.message),
  });
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/announcements/${id}`, { method: "DELETE" });
      await throwIfError(res, "Failed to delete announcement");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: announcementKeys.all });
      toast.success("Announcement deleted");
    },
    onError: (e) => toast.error(e.message),
  });
}
